// Shared location helpers: lat/long parsing and external map/lookup links.
// Used by the Incidents form and the Event Locations management on the Events page.
import { InputAdornment, IconButton, Tooltip, CircularProgress } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";

// Lat/long is one UI field ("lat, long" — what you copy from Google Maps) but two
// numeric columns server-side. Combine on load, parse leniently on save.
export const combineLatLng = (lat, lng) =>
  (lat != null && lat !== "" && lng != null && lng !== "") ? `${lat}, ${lng}` : "";

// Parse one coordinate component to signed decimal degrees, accepting decimal
// degrees ("54.4512", "-3.0125"), degrees-decimal-minutes ("54 27.072") or
// degrees-minutes-seconds ("54°19′46″N"). Any of °, ′, ″, ' , " or plain spaces
// separate the parts, and a leading/trailing hemisphere letter (N/S/E/W) sets the
// sign. Returns null if it can't be parsed to a finite number.
export const parseCoordComponent = (raw) => {
  const s = String(raw ?? "").trim();
  if (!s) return null;
  const hemi = (s.match(/[NSEW]/i) || [])[0]?.toUpperCase();
  const nums = (s.replace(/[NSEW]/ig, " ").match(/-?\d+(?:\.\d+)?/g) || []).map(Number);
  if (!nums.length || nums.some(n => !Number.isFinite(n))) return null;
  const [d, m = 0, sec = 0] = nums;
  const val = Math.abs(d) + m / 60 + sec / 3600;
  const sign = hemi ? (hemi === "S" || hemi === "W" ? -1 : 1) : (d < 0 ? -1 : 1);
  return sign * val;
};

// Decimal-degree places that honestly represent how a component was written, so
// downstream conversions don't overstate precision: decimal degrees keep their
// own dp; degrees+minutes ≈ 2 dp; degrees+minutes+seconds ≈ 4 dp (a second is
// ~30 m), plus any decimals on the finest field.
const coordComponentDp = (raw) => {
  const nums = String(raw ?? "").replace(/[NSEW]/ig, " ").match(/\d+(?:\.\d+)?/g) || [];
  const frac = (x) => { const m = /\.(\d+)$/.exec(String(x ?? "")); return m ? m[1].length : 0; };
  if (nums.length <= 1) return frac(nums[0]);          // decimal degrees
  if (nums.length === 2) return 2 + frac(nums[1]);     // D M(.mmm)
  return Math.min(6, 4 + frac(nums[2]));               // D M S(.sss)
};

export const parseLatLng = (s) => {
  if (!s || !s.trim()) return [null, null];
  const parts = s.split(",").map(x => x.trim());
  if (parts.length !== 2) return [null, null];
  const lat = parseCoordComponent(parts[0]);
  const lng = parseCoordComponent(parts[1]);
  return (Number.isFinite(lat) && Number.isFinite(lng)) ? [lat, lng] : [null, null];
};

// Rewrite a "lat, long" string (decimal or DMS/DDM) as decimal degrees at a
// precision matched to how it was entered, or null if it isn't a valid pair.
// Used to convert pasted DMS to decimal on save / before conversions.
export const normalizeLatLng = (s) => {
  const [lat, lng] = parseLatLng(s);
  if (lat == null) return null;
  const parts = s.split(",").map(x => x.trim());
  const dp = Math.max(coordComponentDp(parts[0]), coordComponentDp(parts[1]));
  return `${lat.toFixed(dp)}, ${lng.toFixed(dp)}`;
};

export const openInNew = (url) => window.open(url, "_blank", "noopener");

// External map/lookup links for the location fields.
export const w3wUrl = (words) => `https://what3words.com/${encodeURIComponent((words || "").trim())}`;
export const mapsUrl = (query) => `https://www.google.co.uk/maps?q=${encodeURIComponent((query || "").trim())}`;
export const gridRefUrl = (gr) => `https://gridreferencefinder.com/index.php?gr=${encodeURIComponent((gr || "").trim())}`;

// Normalise a user-entered link so it opens as an absolute URL (prepend https:// if missing).
export const externalUrl = (u) => {
  const v = (u || "").trim();
  if (!v) return "";
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
};

// --- Grid Reference Finder link → location fields ---------------------------
// gridreferencefinder.com has no API and renders lat/long/postcode client-side
// (the server HTML leaves them blank), so scraping is pointless. But the OS grid
// reference is right there in the link's `gr` query param, and everything except
// what3words can be derived from it: lat/long by pure OSGB36→WGS84 maths here,
// and the nearest postcode via postcodes.io (see Incidents.jsx).

// Pull the OS grid reference out of a Grid Reference Finder URL.
// e.g. ".../os.php?gr=SD5190993629|Point_s_C|1&t=Point%20C" → "SD5190993629".
export const parseGridRefFromUrl = (url) => {
  const v = (url || "").trim();
  if (!v) return null;
  let gr;
  try {
    gr = new URL(/^https?:\/\//i.test(v) ? v : `https://${v}`).searchParams.get("gr");
  } catch {
    return null;
  }
  if (!gr) return null;
  gr = gr.split("|")[0].trim().toUpperCase().replace(/\s+/g, ""); // strip "|label|1" suffix
  return /^[A-Z]{2}\d+$/.test(gr) && gr.length % 2 === 0 ? gr : null; // even digits = equal E/N
};

// Convert an OS grid reference (e.g. "SD5190993629") to WGS84 [lat, lng], or null
// if it can't be parsed. Standard Ordnance Survey algorithm (Airy 1830 → OSGB36
// lat/long, then a Helmert transform to WGS84).
export const osGridRefToLatLng = (gridRef) => {
  const gr = (gridRef || "").trim().toUpperCase().replace(/\s+/g, "");
  const m = gr.match(/^([A-Z])([A-Z])(\d+)$/);
  if (!m || m[3].length % 2 !== 0 || m[3].length > 10) return null;
  const li = (c) => { const v = c.charCodeAt(0) - 65; return v > 7 ? v - 1 : v; }; // grid letters skip 'I'
  const l1 = li(m[1]), l2 = li(m[2]);
  if (l1 > 24 || l2 > 24) return null;
  let e = ((l1 % 5) * 5 + (l2 % 5)) * 100000 - 1000000;
  let n = ((4 - Math.floor(l1 / 5)) * 5 + (4 - Math.floor(l2 / 5))) * 100000 - 500000;
  const half = m[3].length / 2;
  e += parseInt(m[3].slice(0, half), 10) * Math.pow(10, 5 - half);
  n += parseInt(m[3].slice(half), 10) * Math.pow(10, 5 - half);
  if (e < 0 || e > 700000 || n < 0 || n > 1300000) return null; // outside the National Grid

  const a = 6377563.396, b = 6356256.909, F0 = 0.9996012717; // Airy 1830
  const lat0 = (49 * Math.PI) / 180, lon0 = (-2 * Math.PI) / 180;
  const N0 = -100000, E0 = 400000;
  const e2 = 1 - (b * b) / (a * a);
  const nn = (a - b) / (a + b), nn2 = nn * nn, nn3 = nn * nn * nn;

  let lat = lat0, M = 0;
  do {
    lat = (n - N0 - M) / (a * F0) + lat;
    const Ma = (1 + nn + 1.25 * nn2 + 1.25 * nn3) * (lat - lat0);
    const Mb = (3 * nn + 3 * nn2 + 2.625 * nn3) * Math.sin(lat - lat0) * Math.cos(lat + lat0);
    const Mc = (1.875 * nn2 + 1.875 * nn3) * Math.sin(2 * (lat - lat0)) * Math.cos(2 * (lat + lat0));
    const Md = (35 / 24) * nn3 * Math.sin(3 * (lat - lat0)) * Math.cos(3 * (lat + lat0));
    M = b * F0 * (Ma - Mb + Mc - Md);
  } while (Math.abs(n - N0 - M) >= 0.00001);

  const sinLat = Math.sin(lat), cosLat = Math.cos(lat), tanLat = Math.tan(lat);
  const nu = (a * F0) / Math.sqrt(1 - e2 * sinLat * sinLat);
  const rho = (a * F0 * (1 - e2)) / Math.pow(1 - e2 * sinLat * sinLat, 1.5);
  const eta2 = nu / rho - 1;
  const VII = tanLat / (2 * rho * nu);
  const VIII = (tanLat / (24 * rho * Math.pow(nu, 3))) * (5 + 3 * tanLat ** 2 + eta2 - 9 * tanLat ** 2 * eta2);
  const IX = (tanLat / (720 * rho * Math.pow(nu, 5))) * (61 + 90 * tanLat ** 2 + 45 * tanLat ** 4);
  const X = 1 / (cosLat * nu);
  const XI = (1 / (cosLat * 6 * Math.pow(nu, 3))) * (nu / rho + 2 * tanLat ** 2);
  const XII = (1 / (cosLat * 120 * Math.pow(nu, 5))) * (5 + 28 * tanLat ** 2 + 24 * tanLat ** 4);
  const dE = e - E0;
  const latAiry = lat - VII * dE ** 2 + VIII * dE ** 4 - IX * dE ** 6;
  const lonAiry = lon0 + X * dE - XI * dE ** 3 + XII * dE ** 5;

  // Helmert transform: OSGB36 (Airy) → WGS84 (GRS80).
  const sa = Math.sin(latAiry), ca = Math.cos(latAiry), so = Math.sin(lonAiry), co = Math.cos(lonAiry);
  const v = a / Math.sqrt(1 - e2 * sa * sa);
  const x1 = v * ca * co, y1 = v * ca * so, z1 = (1 - e2) * v * sa;
  const tx = 446.448, ty = -125.157, tz = 542.06, s = -20.4894e-6;
  const rx = (0.1502 / 3600) * (Math.PI / 180);
  const ry = (0.247 / 3600) * (Math.PI / 180);
  const rz = (0.8421 / 3600) * (Math.PI / 180);
  const x2 = tx + (1 + s) * x1 - rz * y1 + ry * z1;
  const y2 = ty + rz * x1 + (1 + s) * y1 - rx * z1;
  const z2 = tz - ry * x1 + rx * y1 + (1 + s) * z1;

  const a2 = 6378137.0, b2 = 6356752.3142; // WGS84
  const e22 = 1 - (b2 * b2) / (a2 * a2);
  const p = Math.sqrt(x2 * x2 + y2 * y2);
  let latW = Math.atan2(z2, p * (1 - e22)), prev = 2 * Math.PI;
  while (Math.abs(latW - prev) > 1e-12) {
    prev = latW;
    const v2 = a2 / Math.sqrt(1 - e22 * Math.sin(latW) ** 2);
    latW = Math.atan2(z2 + e22 * v2 * Math.sin(latW), p);
  }
  const lonW = Math.atan2(y2, x2);
  return [(latW * 180) / Math.PI, (lonW * 180) / Math.PI];
};

// --- Precision -------------------------------------------------------------
// A grid ref's figure count and a lat/long's decimal places both encode how
// precise a location is; keep conversions honest by mapping between comparable
// levels rather than inventing digits. Roughly: 10 figures / 5 dp ≈ 1 m,
// 8 / 4 ≈ 10 m, 6 / 3 ≈ 100 m, 4 / 2 ≈ 1 km — i.e. figures ≈ 2 × decimals.
export const figuresToDecimals = (figures) => Math.max(1, Math.min(5, Math.round(figures / 2)));
export const decimalsToFigures = (decimals) => Math.max(2, Math.min(10, decimals * 2));

// Number of figures in a grid ref (digits after the two letters), or null if it
// isn't a well-formed ref (must be an even number of digits, ≤ 10).
export const gridRefFigures = (gridRef) => {
  const m = (gridRef || "").trim().toUpperCase().replace(/\s+/g, "").match(/^[A-Z]{2}(\d+)$/);
  return m && m[1].length % 2 === 0 && m[1].length <= 10 ? m[1].length : null;
};

// Decimal places of a "lat, long" string, taken as the *lesser* of the two so we
// never overstate precision. 0 if it isn't a valid pair.
export const latLngDecimals = (s) => {
  const parts = (s || "").split(",").map(x => (x || "").trim());
  if (parts.length !== 2) return 0;
  const dp = (v) => { const m = /\.(\d+)$/.exec(v); return m ? m[1].length : 0; };
  return Math.min(dp(parts[0]), dp(parts[1]));
};

// Format an OS easting/northing (OSGB36) as a grid reference, e.g. "SD5190993629"
// at 10 figures or "SD519936" at 6. `figures` is the total digit count (even,
// 2–10); null if outside the National Grid.
export const eastingNorthingToGridRef = (E, N, figures = 10) => {
  if (!(E >= 0 && E < 700000 && N >= 0 && N < 1300000)) return null;
  const half = Math.max(1, Math.min(5, Math.round(figures / 2)));
  const e100 = Math.floor(E / 100000), n100 = Math.floor(N / 100000);
  let l1 = (19 - n100) - ((19 - n100) % 5) + Math.floor((e100 + 10) / 5);
  let l2 = (((19 - n100) * 5) % 25) + (e100 % 5);
  if (l1 > 7) l1++; // grid letters skip 'I'
  if (l2 > 7) l2++;
  const letters = String.fromCharCode(l1 + 65, l2 + 65);
  const div = Math.pow(10, 5 - half); // truncate to the containing square, as grid refs do
  const e = String(Math.floor((E % 100000) / div)).padStart(half, "0");
  const n = String(Math.floor((N % 100000) / div)).padStart(half, "0");
  return letters + e + n;
};

// Convert WGS84 [lat, lng] (degrees) to an OS grid reference (default 10 figures;
// pass `figures` to match the source precision), or null if outside the National
// Grid. Inverse of osGridRefToLatLng: WGS84 → (Helmert) OSGB36 lat/long →
// (Transverse Mercator) easting/northing → grid ref.
export const latLngToOsGridRef = (lat, lng, figures = 10) => {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const toRad = Math.PI / 180;
  let phi = lat * toRad, lam = lng * toRad;

  // WGS84 lat/long → WGS84 cartesian
  const aW = 6378137.0, bW = 6356752.3142;
  const e2W = 1 - (bW * bW) / (aW * aW);
  let sp = Math.sin(phi), cp = Math.cos(phi);
  let nu = aW / Math.sqrt(1 - e2W * sp * sp);
  const x = nu * cp * Math.cos(lam), y = nu * cp * Math.sin(lam), z = (1 - e2W) * nu * sp;

  // Helmert WGS84 → OSGB36 (the OSGB36→WGS84 parameters, negated)
  const tx = -446.448, ty = 125.157, tz = -542.06, s = 20.4894e-6;
  const rx = -(0.1502 / 3600) * toRad, ry = -(0.247 / 3600) * toRad, rz = -(0.8421 / 3600) * toRad;
  const x2 = tx + (1 + s) * x - rz * y + ry * z;
  const y2 = ty + rz * x + (1 + s) * y - rx * z;
  const z2 = tz - ry * x + rx * y + (1 + s) * z;

  // OSGB36 cartesian → OSGB36 lat/long (Airy 1830)
  const a = 6377563.396, b = 6356256.909;
  const e2 = 1 - (b * b) / (a * a);
  const p = Math.sqrt(x2 * x2 + y2 * y2);
  phi = Math.atan2(z2, p * (1 - e2));
  let prev = 2 * Math.PI;
  while (Math.abs(phi - prev) > 1e-12) {
    prev = phi;
    nu = a / Math.sqrt(1 - e2 * Math.sin(phi) ** 2);
    phi = Math.atan2(z2 + e2 * nu * Math.sin(phi), p);
  }
  lam = Math.atan2(y2, x2);

  // OSGB36 lat/long → easting/northing (National Grid Transverse Mercator)
  const F0 = 0.9996012717;
  const phi0 = 49 * toRad, lam0 = -2 * toRad, N0 = -100000, E0 = 400000;
  const n = (a - b) / (a + b), n2 = n * n, n3 = n * n * n;
  sp = Math.sin(phi); cp = Math.cos(phi);
  const tanPhi = Math.tan(phi);
  const nuF = (a * F0) / Math.sqrt(1 - e2 * sp * sp);
  const rhoF = (a * F0 * (1 - e2)) / Math.pow(1 - e2 * sp * sp, 1.5);
  const eta2 = nuF / rhoF - 1;
  const Ma = (1 + n + 1.25 * n2 + 1.25 * n3) * (phi - phi0);
  const Mb = (3 * n + 3 * n2 + 2.625 * n3) * Math.sin(phi - phi0) * Math.cos(phi + phi0);
  const Mc = (1.875 * n2 + 1.875 * n3) * Math.sin(2 * (phi - phi0)) * Math.cos(2 * (phi + phi0));
  const Md = (35 / 24) * n3 * Math.sin(3 * (phi - phi0)) * Math.cos(3 * (phi + phi0));
  const M = b * F0 * (Ma - Mb + Mc - Md);
  const I = M + N0;
  const II = (nuF / 2) * sp * cp;
  const III = (nuF / 24) * sp * cp ** 3 * (5 - tanPhi ** 2 + 9 * eta2);
  const IIIA = (nuF / 720) * sp * cp ** 5 * (61 - 58 * tanPhi ** 2 + tanPhi ** 4);
  const IV = nuF * cp;
  const V = (nuF / 6) * cp ** 3 * (nuF / rhoF - tanPhi ** 2);
  const VI = (nuF / 120) * cp ** 5 * (5 - 18 * tanPhi ** 2 + tanPhi ** 4 + 14 * eta2 - 58 * tanPhi ** 2 * eta2);
  const dLam = lam - lam0;
  const N = I + II * dLam ** 2 + III * dLam ** 4 + IIIA * dLam ** 6;
  const E = E0 + IV * dLam + V * dLam ** 3 + VI * dLam ** 5;
  return eastingNorthingToGridRef(E, N, figures);
};

// An "open in new tab" icon button as a TextField end adornment, disabled when empty.
export const openAdornment = (url, enabled, tip) => ({
  input: {
    endAdornment: (
      <InputAdornment position="end">
        <Tooltip title={tip}>
          <span>
            <IconButton edge="end" size="small" disabled={!enabled} onClick={() => openInNew(url)}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </InputAdornment>
    ),
  },
});

// A TextField end adornment with a "populate the other fields from this one"
// (magic-wand) button, followed by the existing "open in..." button.
//   populate = { onClick, enabled, busy, tip }
//   open     = { url, enabled, tip }
export const convertAdornment = (populate, open) => ({
  input: {
    endAdornment: (
      <InputAdornment position="end">
        <Tooltip title={populate.tip}>
          <span>
            <IconButton edge="end" size="small" disabled={!populate.enabled || populate.busy}
                        onClick={populate.onClick}>
              {populate.busy ? <CircularProgress size={16} /> : <AutoFixHighIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={open.tip}>
          <span>
            <IconButton edge="end" size="small" disabled={!open.enabled} onClick={() => openInNew(open.url)}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </InputAdornment>
    ),
  },
});
